import * as vp from 'vopay';
import { Box, Select, TextField, DateRangePicker } from 'vopay';

import { FunctionComponent, useState } from 'react';
import styles from './ClientAccountTransfersForm.module.scss';
import { useTranslation } from 'react-i18next';

import { Button } from '#shared/components/UI';

type ScheduledClientAccountTransactionSearchParams = {
  transferType: string;
  frequency: string;
  status: string;
  searchTerm: string;
  timeRange: [vp.Timestamp, vp.Timestamp];
};

type SearchFormProps = {
  transferType: string;
  frequency: string;
  status: string;
  searchTerm: string;
  initialTimeRange: [vp.Timestamp, vp.Timestamp];
  hasInvalidEndDate?: boolean;
  shouldShowEndDateError?: boolean;
  setShouldShowEndDateError?: (event: boolean) => void;
  onSearch: (searchParams: ScheduledClientAccountTransactionSearchParams) => void;
};

type FC = FunctionComponent<SearchFormProps>;

const ClientAccountTransfersForm: FC = (props: SearchFormProps) => {
  const {
    transferType,
    frequency,
    status,
    searchTerm,
    hasInvalidEndDate,
    shouldShowEndDateError,
    setShouldShowEndDateError,
    initialTimeRange,
    onSearch,
  } = props;

  const [currentTransferType, setCurrentTransferType] = useState<string>(transferType);
  const [currentFrequency, setCurrentFrequency] = useState<string>(frequency);
  const [currentStatus, setCurrentStatus] = useState<string>(status);
  const [currentSearchTerm, setCurrentSearchTerm] = useState<string>(searchTerm);
  const [timeRange, setTimeRange] = useState<[vp.Timestamp, vp.Timestamp]>(initialTimeRange);
  const { t } = useTranslation('translations', { keyPrefix: 'Shared.ClientAccounts.Schedule' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Shared' });

  const transferTypeOption = [
    { label: tGlobal('TransferTypeOptions.all', { defaultValue: 'All' }), value: 'all' },
    {
      label: tGlobal('TransferTypeOptions.fundTransfer', { defaultValue: 'Fund and Transfer' }),
      value: 'fund-transfer',
    },
    {
      label: tGlobal('TransferTypeOptions.transferWithdraw', { defaultValue: 'Transfer and Withdraw' }),
      value: 'transfer-withdraw',
    },
    {
      label: tGlobal('TransferTypeOptions.fundTransferWithdraw', { defaultValue: 'Fund, Transfer and Withdraw' }),
      value: 'fund-transfer-withdraw',
    },
  ];

  const frequencyOptions = [
    { label: tGlobal('FrequencyOptions.all', { defaultValue: 'All' }), value: 'all' },
    { label: tGlobal('FrequencyOptions.single', { defaultValue: 'Single' }), value: 'single' },
    { label: tGlobal('FrequencyOptions.monthly', { defaultValue: 'Monthly' }), value: 'monthly' },
    { label: tGlobal('FrequencyOptions.weekly', { defaultValue: 'Weekly' }), value: 'weekly' },
    { label: tGlobal('FrequencyOptions.semiMonthly', { defaultValue: 'Semi-Monthly' }), value: 'semi-monthly' },
    { label: tGlobal('FrequencyOptions.annually', { defaultValue: 'Annually' }), value: 'yearly' },
  ];

  const statusOptions = [
    { label: t('SearchForm.Status.Options.all', { defaultValue: 'All' }), value: 'all' },
    { label: t('SearchForm.Status.Options.cancelled', { defaultValue: 'Cancelled' }), value: 'cancelled' },
    { label: t('SearchForm.Status.Options.scheduled', { defaultValue: 'Scheduled' }), value: 'scheduled' },
    { label: t('SearchForm.Status.Options.completed', { defaultValue: 'Completed' }), value: 'completed' },
    { label: t('SearchForm.Status.Options.inProgress', { defaultValue: 'In Progress' }), value: 'in progress' },
  ];

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();

        onSearch({
          transferType: currentTransferType,
          searchTerm: currentSearchTerm,
          frequency: currentFrequency,
          status: currentStatus,
          timeRange,
        });
      }}
    >
      <Box flx="row" gap="sm" wrap="wrap" ai="center">
        <div className={styles.datePickerContainer}>
          <DateRangePicker
            onChange={(range) => {
              setTimeRange(range);
            }}
            triggerVariant="outlineShadow"
            initialTime={timeRange}
            hasInvalidEndDate={hasInvalidEndDate}
            shouldShowEndDateError={shouldShowEndDateError}
            setShouldShowEndDateError={setShouldShowEndDateError}
          />
        </div>

        <Box width="200px">
          <Select
            version="2"
            placeholder={t('SearchForm.transferTypePlaceholder', { defaultValue: 'Transfer Type' })}
            options={transferTypeOption}
            name="scheduleType"
            value={currentTransferType}
            onBlur={() => {}}
            onChange={(e) => {
              setCurrentTransferType(e.target.value);
            }}
            sort={false}
          />
        </Box>

        <Box width="200px">
          <Select
            version="2"
            placeholder={t('SearchForm.frequencyPlaceholder', { defaultValue: 'Frequency' })}
            options={frequencyOptions}
            name="frequency"
            value={currentFrequency}
            onBlur={() => {}}
            onChange={(e) => {
              setCurrentFrequency(e.target.value);
            }}
            sort={false}
          />
        </Box>

        <Box width="180px">
          <Select
            version="2"
            placeholder={t('SearchForm.Status.placeholder', { defaultValue: 'Status' })}
            options={statusOptions}
            name="status"
            value={currentStatus}
            onBlur={() => {}}
            onChange={(e) => {
              setCurrentStatus(e.target.value);
            }}
            sort={false}
          />
        </Box>

        <Box maxwidth="180px">
          <TextField
            key="AccountSearchKeyword"
            name="AccountSearchKeyword"
            placeholder={t('SearchForm.keywordPlaceholder', { defaultValue: 'Keyword' })}
            value={currentSearchTerm}
            onBlur={() => {}}
            onChange={(e) => {
              setCurrentSearchTerm(e.target.value);
            }}
            variant="outlineShadow"
          />
        </Box>

        <Button type="submit" id="search-schedule-transactions" size="small" colorScheme="primary">
          {t('SearchForm.submitButton', { defaultValue: 'Search' })}
        </Button>
      </Box>
    </form>
  );
};

export default ClientAccountTransfersForm;
